import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import type { MatchStatePayload, Player } from '@qlash-it/qchess-board-ui';

import { Button } from '@/components/common/Button';
import { SopraLaBarra } from '@/components/common/SopraLaBarra';
import { Sheet } from '@/components/common/Sheet';
import { offertaPattaVisibile } from '@/lib/chess/offertaPatta';

/**
 * I comandi del tavolo degli scacchi (reference 07/10, punto 4 e 7): offrire, accettare, rifiutare e rivendicare la patta, e
 * abbandonare con un FOGLIO dal basso di conferma.
 *
 * Sono i comandi della board-ui (`ChessControls`) senza i suoi bottoni: quelli non hanno stile (era «Offri pattaAbbandona» nel
 * web) e il doppio tocco e' un Tweak del canvas, NON la scelta approvata (L-2928). Qui la LOGICA e' la stessa — `drawOffer`,
 * `claimableDraw`, `status` — e il game host resta l'unico giudice: ogni bottone solo richiama.
 *
 * ⚠️ «Abbandona» concede l'INCONTRO INTERO (QChess `handlers.ts:421-430`), non la sola partita: nella «decisiva» il foglio lo
 * dice (`resign.titleMatch`). Il foglio e' un livello di `Sheet`, quindi il tasto indietro di Telegram lo chiude senza
 * lasciare il tavolo.
 */
export function ControlliPartita({
  state,
  mio,
  disabled,
  decisiva,
  inTorneo = false,
  onOffer,
  onRespond,
  onClaim,
  onResign,
}: {
  state: MatchStatePayload;
  /** Il MIO colore in QUESTA partita (`myColour(model)`). */
  mio: Player;
  disabled: boolean;
  /** La modalita' e' «decisiva»: l'abbandono perde l'incontro intero, e il foglio lo dice. */
  decisiva: boolean;
  /**
   * La partita e' di un torneo VERO (non una sfida fra amici). Default `false` dal 07/10 22:0xZ (I-493, Luca vuole sfidare a
   * scacchi): «Perdi per abbandono.» e' vera in OGNI contesto, «…e il risultato va al torneo» solo in un torneo. Chi monta il
   * tavolo oggi non sa distinguere una sfida da un torneo (lo state della porta non lo porta), quindi non lo passa: meglio la
   * frase generica in un torneo che quella del torneo in una sfida. Cablarlo (l'`origine` del torneo, `nonEUnaSfida`) e' un
   * seguito di P4.
   */
  inTorneo?: boolean;
  onOffer: () => void;
  onRespond: (accept: boolean) => void;
  onClaim: () => void;
  onResign: () => void;
}) {
  const { t } = useTranslation('chessMatch');
  const [conferma, setConferma] = useState(false);
  if (state.status === 'ended') return null;

  const offerta = state.drawOffer;
  const aMe = offerta !== null && offerta.by !== mio;
  const daMe = offerta !== null && offerta.by === mio;
  const rivendicabile = state.claimableDraw;
  const abbandona = (
    <Button variant="ghost" forma="pastiglia" disabled={disabled} data-action="resign" onClick={() => setConferma(true)}>
      <Flag size={14} aria-hidden />
      {t('controls.resign')}
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 px-1" role="group" data-testid="chess-controlli">
      {aMe ? (
        <>
          <span className="rounded-xl bg-bg-secondary px-3.5 py-2.5 text-sm font-semibold text-warning" role="status" data-testid="chess-patta-ricevuta">
            {t('controls.drawReceived')}
          </span>
          {/* Il testo e' CORTO perche' «la patta» la dice gia' la pastiglia accanto (a 360×640 la riga deve stare sopra la barra, I-591);
              l'aria-label porta la frase intera per chi legge con lo schermo. */}
          <Button disabled={disabled} data-action="accept-draw" aria-label={t('controls.acceptDrawLabel')} onClick={() => onRespond(true)}>
            {t('controls.acceptDraw')}
          </Button>
          <Button variant="danger" disabled={disabled} data-action="decline-draw" onClick={() => onRespond(false)}>
            {t('controls.declineDraw')}
          </Button>
        </>
      ) : daMe ? (
        <>
          <span className="rounded-xl bg-bg-secondary px-3.5 py-2.5 text-sm font-semibold text-fg-muted" role="status" data-testid="chess-patta-inviata">
            {t('controls.drawSent')}
          </span>
          {abbandona}
        </>
      ) : rivendicabile !== null ? (
        <>
          <div className="flex flex-col items-center gap-0.5">
            <Button disabled={disabled} data-action="claim-draw" data-ground={rivendicabile} onClick={onClaim}>
              {t('controls.claimDraw')}
            </Button>
            <span className="text-[11px] font-semibold text-fg-muted">
              {t(rivendicabile === 'threefold_repetition' ? 'controls.claimThreefold' : 'controls.claimFifty')}
            </span>
          </div>
          {abbandona}
        </>
      ) : (
        <>
          {/* Nella pratica contro il bot «Offri patta» esce dal DOM (il bot rifiuta sempre): v. `offertaPattaVisibile`. */}
          {offertaPattaVisibile(state) && (
            <Button
              variant="secondary"
              disabled={disabled}
              data-action="offer-draw"
              className="border border-border"
              onClick={onOffer}
            >
              {t('controls.offerDraw')}
            </Button>
          )}
          {abbandona}
        </>
      )}

      {/* Il foglio sta SOPRA la barra in basso: nasce dentro la schermata `isolate` del tavolo, e senza portale ne avrebbe gli
          ultimi pixel sotto la barra (v. `SopraLaBarra`; il backgammon lo fa in `ResignControls`). */}
      <SopraLaBarra>
        <Sheet open={conferma} onClose={() => setConferma(false)} label={t(decisiva ? 'resign.titleMatch' : 'resign.titleGame')}>
          <div className="flex flex-col gap-3 px-4 pb-7 pt-5" data-testid="chess-conferma-abbandono">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-danger/15 text-[#ff6b66]">
              <Flag size={20} aria-hidden />
            </span>
            <h2 className="text-[17px] font-bold text-fg">{t(decisiva ? 'resign.titleMatch' : 'resign.titleGame')}</h2>
            <p className="text-sm leading-snug text-fg-muted">{t(decisiva ? 'resign.bodyMatch' : inTorneo ? 'resign.bodyGame' : 'resign.bodyGameFree')}</p>
            <div className="mt-1 flex gap-2.5">
              <Button variant="secondary" className="h-12 flex-1 border border-border" data-action="resign-cancel" onClick={() => setConferma(false)}>
                {t('resign.cancel')}
              </Button>
              <Button
                className="h-12 flex-1 bg-danger text-white"
                peso="bold"
                data-action="resign-confirm"
                onClick={() => {
                  setConferma(false);
                  onResign();
                }}
              >
                {t('resign.confirm')}
              </Button>
            </div>
          </div>
        </Sheet>
      </SopraLaBarra>
    </div>
  );
}
